import * as fromAssignmentsActions from "../actions/assignments.action";

// Interfaces
import { AssignmentPagination, AssignmentPaginationParameters } from "@carnegie-learning/data-access-angular-passport";
import { Action, createReducer, on } from "@ngrx/store";
import { PPAssignment as Assignment } from "cli-types";

// creating an interface to say what type the initialState will be
// using entities (data objects) instead of arrays is better for performance
export interface AssignmentsListState {
    entities: Map<number, Assignment>;
    loaded: boolean;
    loading: boolean;
    rawResponse: AssignmentPagination | null;
    paginationParams: AssignmentPaginationParameters;
    displayFilterParams: any;
    selectedAssignment: Assignment | null;
    selectedAssignmentID: number | null;
    selectedAssignmentActivityID: number | null;
    setLaunchedInNewTab: number | null;
}

// set initial state as type AssignmentsListState
export const initialState: AssignmentsListState = {
    entities: new Map<number, Assignment>(),
    loaded: false,
    loading: false,
    paginationParams: {
        studentID: null,
        filter: null,
        limit: 30,
        page: 1,
        search: null,
        sorting: {},
        courseID: null,
        withCourseMetrics: false,
        total: null,
    },
    displayFilterParams: [],
    rawResponse: null,
    selectedAssignment: null,
    selectedAssignmentID: null,
    selectedAssignmentActivityID: null,
    setLaunchedInNewTab: null,
};

// 1st argument must be the initial state
// 2nd argument is the action (of type... any action type imported from action file)
// the state returned will be of type AssignmentsListState
const assignmentsReducer = createReducer(
    initialState,
    on(fromAssignmentsActions.LoadStudentAssignment, (state, {}) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    }),
    on(
        fromAssignmentsActions.LoadStudentAssignmentSuccess,
        (state, { assignment }) => {
            const assignmentID = assignment.assignmentID;

            // TODO is this suppose to be additive? - Kao
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );

            entities.delete(assignmentID);
            entities.set(assignmentID, assignment);

            // then re-bind the entity const with our returned state
            return {
                ...state,
                loading: false,
                loaded: true,
                entities: entities,
                selectedAssignment: assignment,
            };
        }
    ),
    on(
        fromAssignmentsActions.updateStudentAssignmentSuccess,
        (state, { assignment }) => {
            const assignmentID = assignment.assignmentID;
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );
            const oriAssignment = entities.get(assignmentID);
            const updatedAssignment = {
                ...oriAssignment,
                ...assignment,
            };
            entities.set(assignmentID, updatedAssignment);

            return {
                ...state,
                loading: false,
                loaded: true,
                entities: entities,
                selectedAssignment: updatedAssignment,
            };
        }
    ),
    on(fromAssignmentsActions.LoadStudentAssignmentFail, (state, {}) => {
        return {
            ...state,
            loading: false,
            loaded: false,
        };
    }),
    on(fromAssignmentsActions.LoadStudentAssignments, (state, { payload }) => {
        return {
            ...state,
            loading: true,
            loaded: false,
            paginationParams: payload,
        };
    }),
    on(
        fromAssignmentsActions.LoadStudentAssignmentsSuccess,
        (state, { AssignmentPaginationData }) => {
            const assignments = AssignmentPaginationData.data;

            // For student assignments, entities are additive.
            let entities: Map<number, Assignment>;
            if (state.paginationParams.page > 1) {
                entities = new Map<number, Assignment>(
                    state.entities.entries()
                );
            } else {
                entities = new Map<number, Assignment>();
            }

            assignments.forEach((assignment) => {
                // use the selected assignment if available so the full assignment object isn't overwritten -LM
                if (
                    state.selectedAssignment &&
                    state.selectedAssignment.assignmentID ===
                        assignment.assignmentID &&
                    state.selectedAssignment.status === assignment.status
                ) {
                    entities.delete(assignment.assignmentID);
                    entities.set(
                        assignment.assignmentID,
                        state.selectedAssignment
                    );
                } else {
                    entities.delete(assignment.assignmentID);
                    entities.set(assignment.assignmentID, assignment);
                }
            });

            // then re-bind the entity const with our returned state
            return {
                ...state,
                loading: false,
                loaded: true,
                entities: entities,
                rawResponse: AssignmentPaginationData,
            };
        }
    ),
    on(fromAssignmentsActions.LoadStudentAssignmentsFail, (state, {}) => {
        return {
            ...state,
            loading: false,
            loaded: false,
        };
    }),
    on(
        fromAssignmentsActions.GetStudentAssignmentFromTransIdSuccess,
        (state, { assignment }) => {
            return {
                ...state,
                loading: false,
                loaded: false,
                selectedAssignmentID: assignment.assignmentID,
                selectedAssignment: assignment,
            };
        }
    ),

    on(fromAssignmentsActions.SetPaginationParams, (state, { payload }) => {
        // Merge parameter values, don't overwrite like LOAD_STUDENT_ASSIGNMENTS does.
        const inputParams = payload;
        const studentPaginationParams: any = { ...state.paginationParams }; // TODO: figure out a better way to do this. Should be AssignmentPaginationParameters, not any, but that offends line 168.
        const filter: { [index: string]: any } = {}; // The filter element on the AssignmentPaginationParameters type has an explicit type of any.
        Object.keys(inputParams).forEach(function (key) {
            if (key === "filter" && studentPaginationParams.filter) {
                if (inputParams.filter.status) {
                    // add status
                    filter["status"] = inputParams.filter.status;
                }
                if (inputParams.filter.shortAssignableType) {
                    // add short assignable type
                    filter["shortAssignableType"] =
                        inputParams.filter.shortAssignableType;
                }
                if (inputParams.filter.assignableType) {
                    // add assignable type
                    filter["assignableType"] =
                        inputParams.filter.assignableType;
                }
                studentPaginationParams[key] = filter;
            } else {
                studentPaginationParams[key] = inputParams[key];
            }
        });

        return {
            ...state,
            paginationParams: studentPaginationParams,
        };
    }),
    on(fromAssignmentsActions.SetDisplayFilterParams, (state, { payload }) => {
        return {
            ...state,
            displayFilterParams: payload,
        };
    }),
    on(
        fromAssignmentsActions.UpdateStudentAssignmentSuccess,
        (state, { payload }) => {
            const assignment = payload;

            // create an updated representation of our entities (data with new teacher-class)
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );
            entities.delete(assignment.assignmentID);
            entities.set(assignment.assignmentID, assignment);

            return {
                ...state,
                entities: entities,
            };
        }
    ),
    on(fromAssignmentsActions.SetSelectedAssignment, (state, { payload }) => {
        if (
            state?.selectedAssignment?.assignmentID === payload?.assignmentID &&
            !payload?.asset &&
            state?.selectedAssignment?.asset
        ) {
            payload = {
                ...payload,
                asset: state?.selectedAssignment?.asset,
            };
        }
        return {
            ...state,
            selectedAssignment: payload,
        };
    }),
    on(fromAssignmentsActions.SetSelectedAssignmentID, (state, { payload }) => {
        return {
            ...state,
            selectedAssignmentID: payload,
        };
    }),
    on(
        fromAssignmentsActions.SetSelectedAssignmentActivityID,
        (state, { payload }) => {
            return {
                ...state,
                selectedAssignmentActivityID: payload,
            };
        }
    ),
    on(fromAssignmentsActions.ClearAssignments, (state, {}) => {
        return {
            ...state,
            rawResponse: null,
            entities: new Map<number, Assignment>(),
            loaded: false,
            loading: false,
            selectedAssignment: null,
            paginationParams: {
                studentID: null,
                filter: null,
                limit: 30,
                page: 1,
                search: null,
                sorting: {},
                courseID: null,
                withCourseMetrics: false,
                total: null,
            },
        };
    }),
    on(fromAssignmentsActions.ClearAssignmentEntities, (state, {}) => {
        return {
            ...state,
            entities: new Map<number, Assignment>(),
            selectedAssignment: null,
        };
    }),
    on(fromAssignmentsActions.SetLaunchedInNewTab, (state, { payload }) => {
        return {
            ...state,
            setLaunchedInNewTab: payload,
        };
    }),
    on(
        fromAssignmentsActions.LoadAssignmentAssetSuccess,
        (state, { assignmentId, asset }) => {
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );

            let assignment = entities.get(assignmentId);
            if (asset && assignment) {
                assignment = { ...assignment, asset: asset };
                entities.set(assignmentId, assignment);
            }

            let updatedSelectedAssignment = state.selectedAssignment;
            if (
                asset &&
                updatedSelectedAssignment &&
                updatedSelectedAssignment.assignmentID === assignmentId
            ) {
                updatedSelectedAssignment = {
                    ...updatedSelectedAssignment,
                    asset: asset,
                };
            }

            return {
                ...state,
                entities: entities,
                selectedAssignment: updatedSelectedAssignment,
            };
        }
    ),
    on(
        fromAssignmentsActions.LoadAllStudentAssignmentAssetsSuccess,
        (state, { assignments, assets }) => {
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );

            if (assignments && assets) {
                assignments.forEach((assignment) => {
                    const foundAsset = assets.find(
                        (asset) => asset.assetId === assignment.assetId
                    );
                    assignment = { ...assignment, asset: foundAsset };
                    entities.set(assignment.assignmentID, assignment);
                });
            }
            return {
                ...state,
                entities: entities,
            };
        }
    ),
    on(
        fromAssignmentsActions.loadBundleAssignmentsAssetsSuccess,
        (state, { bundleAssetId, compositeTaskAssignments, assets }) => {
            const updatedAssignment: Assignment = {
                ...(<Assignment>state.selectedAssignment),
            };
            if (compositeTaskAssignments && assets) {
                compositeTaskAssignments = compositeTaskAssignments.map(
                    (assignment) => {
                        const foundAsset = assets.find(
                            (asset) => asset.assetId === assignment.assetId
                        );
                        return (assignment = {
                            ...assignment,
                            asset: foundAsset,
                        });
                    }
                );
            }

            updatedAssignment.compositeTaskAssignments =
                compositeTaskAssignments;

            return {
                ...state,
                selectedAssignment: updatedAssignment,
            };
        }
    )
);

// exporting the reducer so it can be added to the index file
export function reducer(
    state: AssignmentsListState | undefined,
    action: Action
) {
    return assignmentsReducer(state, action);
}

// this is where we want to export some levels of state
export const getStudentAssignmentsEntities = (state: AssignmentsListState) =>
    state.entities;
export const getStudentAssignmentsLoading = (state: AssignmentsListState) =>
    state.loading;
export const getStudentAssignmentsLoaded = (state: AssignmentsListState) =>
    state.loaded;
export const getSelectedAssignment = (state: AssignmentsListState) =>
    state.selectedAssignment;
export const getPaginationParams = (state: AssignmentsListState) =>
    state.paginationParams;
export const getDisplayFilterParams = (state: AssignmentsListState) =>
    state.displayFilterParams;
export const getRawResponse = (state: AssignmentsListState) =>
    state.rawResponse;
export const getSelectedAssignmentID = (state: AssignmentsListState) =>
    state.selectedAssignmentID;
export const getSelectedAssignmentActivityID = (state: AssignmentsListState) =>
    state.selectedAssignmentActivityID;
export const getLaunchedInNewTab = (state: AssignmentsListState) =>
    state.setLaunchedInNewTab;
